import { useEffect, useMemo, useState } from 'react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import { ScrollText, ShoppingCart, Store } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import {
  DataTableCampaignSelectFilter,
  DataTableSelectFilter,
} from '../../../components/table/data-table-filters.jsx';
import { DataTableFooter } from '../../../components/table/data-table-footer.jsx';
import {
  DataTableFacetedFilter,
  DataTableToolbar,
} from '../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Badge } from '../../../components/ui/badge.jsx';
import { DebouncedInput } from '../../../components/ui/input-debounced.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { useQueryActiveCampaign } from '../../../hooks/queries/use-active-campaign.jsx';
import { COUPONS_STATES, TABLE_FILTER_LIKE } from '../../../utils/constants.js';
import { filterParams } from '../../../utils/filters.js';

// Query string keys of the deep links pointing to this page that do not match
// the respective table column id.
const SEARCH_PARAM_COLUMN_IDS = {
  campaignId: 'coupons.campaignId',
};

const TABLE_COLUMNS = [
  {
    title: 'Código',
    accessorKey: 'ROWID',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Código" />
    ),
    cell: ({ row }) => <div>{row.getValue('ROWID')}</div>,
    enableSorting: true,
    enableHiding: false,
  },
  {
    title: 'Estado',
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const couponStatus = row.getValue('status');

      return couponStatus === COUPONS_STATES.VALID ? (
        <Badge className="min-w-24 justify-center" variant="success">
          {couponStatus}
        </Badge>
      ) : (
        <Badge className="min-w-24 justify-center" variant="outline">
          {couponStatus}
        </Badge>
      );
    },
    enableSorting: true,
    enableHiding: false,
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    id: 'customers.fullName',
    title: 'Cliente',
    accessorKey: 'customer',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Cliente" />
    ),
    cell: ({ row }) => {
      if (row.original.customerId) {
        return (
          <Link to={`/backoffice/customers/${row.original.customerId}`}>
            <div className="underline">{row.original.customer}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    id: 'coupons.campaignId',
    title: 'Campanha',
    accessorKey: 'campaign',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Campanha" />
    ),
    cell: ({ row }) => {
      if (row.original.campaignId) {
        return (
          <Link to={`/backoffice/campaigns/${row.original.campaignId}`}>
            <div className="underline">{row.original.campaign}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    id: 'shops.name',
    title: 'Loja',
    accessorKey: 'shop',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
    cell: ({ row }) => {
      if (row.original.shopId) {
        return (
          <Link to={`/backoffice/admin/shops/${row.original.shopId}`}>
            <div className="underline">{row.original.shop}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    id: 'invoices.code',
    title: 'Fatura',
    accessorKey: 'invoiceCode',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fatura" />
    ),
    cell: ({ row }) => {
      if (row.original.invoiceId) {
        return (
          <Link to={`/backoffice/invoices/${row.original.invoiceId}`}>
            <div className="underline">{row.original.invoiceCode}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
];

export function CouponsList() {
  const [searchParams] = useSearchParams();
  const { isAdmin } = useSnapshot(authState);

  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: 'createdTime',
      desc: true,
    },
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const queryActiveCampaign = useQueryActiveCampaign();

  const queryCoupons = useQuery({
    queryKey: ['coupons', pagination, columnFilters, sorting],
    queryFn: async () => {
      let filters = filterParams(table);

      const { data: coupons } = await msCatalystApi.get(
        `coupons/paginate?${filters}`,
      );

      return coupons.data;
    },
    placeholderData: keepPreviousData,
    select: (data) => {
      return {
        rows: data.rows.map((item) => ({
          ROWID: item.coupons.ROWID,
          createdTime: item.coupons.CREATEDTIME,
          code: item.coupons.code,
          status: item.coupons.status,
          invoiceCode: item.invoices.code,
          invoiceAmount: item.invoices.amount,
          invoiceId: item.invoices.ROWID,
          customer: `${item.customers.firstName} ${item.customers.lastName}`,
          customerId: item.customers.ROWID,
          campaign: item.campaigns.name,
          campaignId: item.campaigns.ROWID,
          shop: item.shops.name,
          shopId: item.shops.ROWID,
        })),
      };
    },
    enabled: !queryActiveCampaign.isLoading,
  });

  const defaultData = useMemo(() => [], []);

  const table = useReactTable({
    data: queryCoupons.data?.rows ?? defaultData,
    columns: TABLE_COLUMNS,
    rowCount: queryCoupons.data?.rowCount,
    state: {
      pagination,
      sorting,
      columnFilters,
      rowSelection,
      columnVisibility,
    },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: (val) => {
      setColumnFilters(val);
      if (val.length) setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection: true,
    manualPagination: true,
  });

  useEffect(() => {
    if (
      queryActiveCampaign.isSuccess &&
      queryActiveCampaign?.data &&
      !columnFilters.length
    ) {
      setColumnFilters([
        {
          id: 'coupons.campaignId',
          value: [queryActiveCampaign.data?.ROWID],
        },
      ]);
    }
  }, [queryActiveCampaign.isSuccess]);

  useEffect(() => {
    if (searchParams.size > 0) {
      const paramFilters = [];

      searchParams.forEach((value, key) => {
        const columnId = SEARCH_PARAM_COLUMN_IDS[key] ?? key;

        if (!table.getColumn(columnId)) return;

        paramFilters.push({
          id: columnId,
          value: key === 'status' ? value.split(',') : [value],
        });
      });

      if (paramFilters.length) {
        setColumnFilters((prev) => [
          ...prev.filter(
            (filter) => !paramFilters.some((item) => item.id === filter.id),
          ),
          ...paramFilters,
        ]);
      }
    }
  }, [searchParams]);

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Cupões</Heading>
      </div>

      {queryCoupons.isLoading ||
      queryCoupons.isPending ||
      queryActiveCampaign.isLoading ||
      queryActiveCampaign.isPending ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryCoupons.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          {/* TABLE HEADER */}
          <DataTableToolbar table={table}>
            <DebouncedInput
              placeholder={`Pesquisar código`}
              value={table.getColumn('ROWID')?.getFilterValue() ?? ''}
              onChange={(val) => table.getColumn('ROWID')?.setFilterValue(val)}
              className="h-10 md:w-[255px]"
            />

            {table.getColumn('status') && (
              <DataTableFacetedFilter
                column={table.getColumn('status')}
                title="Estado"
                search={false}
                options={[
                  { label: 'Válido', value: COUPONS_STATES.VALID },
                  { label: 'Anulado', value: COUPONS_STATES.VOIDED },
                ]}
              />
            )}

            {isAdmin && table.getColumn('coupons.campaignId') && (
              <DataTableCampaignSelectFilter
                column={table.getColumn('coupons.campaignId')}
                title="Campanhas"
                table="campaigns"
                tableFilterCol="name"
                valueCol="ROWID"
                query={queryActiveCampaign}
              />
            )}

            {table.getColumn('customers.fullName') && (
              <DataTableSelectFilter
                column={table.getColumn('customers.fullName')}
                title="Clientes"
                table="customers"
                tableFilterCol="fullName"
                icon={<ShoppingCart />}
              />
            )}

            {table.getColumn('shops.name') && (
              <DataTableSelectFilter
                column={table.getColumn('shops.name')}
                title="Lojas"
                table="shops"
                tableFilterCol="name"
                icon={<Store />}
              />
            )}

            {table.getColumn('invoices.code') && (
              <DataTableSelectFilter
                column={table.getColumn('invoices.code')}
                title="Faturas"
                table="invoices"
                tableFilterCol="code"
                querySelect={(data) => {
                  return data.map((item) => item.invoices);
                }}
                icon={<ScrollText />}
              />
            )}
          </DataTableToolbar>

          {/* TABLE */}
          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {queryCoupons.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryCoupons.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Cupões na base de dados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* TABLE FOOTER */}
          <DataTableFooter hide={{ selectedRows: true }} table={table} />
        </div>
      )}
    </>
  );
}
